/* eslint-disable max-lines -- Temp until we can get this file to be under 300 lines long! */
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs';

import { CatsActionModeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { findUnique } from '@shared-helpers/array-object-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import { IForemanCatsProperties, IForemanEmployee, IHttpResponseEquipment, ISkfConfigEntity } from '@shared-interfaces/api';
import { IOutgoingCatRecords } from '@shared-interfaces/cats-db';

import { Employee } from '../classes/employee/employee';
import { BulkEntryComponent } from '../components/bulk-entry/bulk-entry.component';
import {
  IAnyCatsRecord,
  ICreateEmployeeBillable,
  ICreateEquipment,
  ICreateProductionEquipment,
  ICreateProductionQuantity,
  ICreateProductionText,
  IModifyCatsRecordHours,
  IModifyCatsRecordQuantity,
  IModifyCatsRecordText,
} from '../models/cats-outbound.model';
import { DatahubService } from '../services/datahub.service';
import { Equipment } from './equipment';
import { ProductionEquipment } from './productionEquipment';
import { ProductionGroup } from './productionGroup';
import { ProductionQuantity } from './productionQuantity';
import { ProductionText } from './productionText';

/**
 * @description All the various application entities all together in a single strong type
 * @todo Eventually these classes should all inherit from a base class and we can use that instead of this type
 */
type IEntity = Employee | Equipment | ProductionText | ProductionGroup | ProductionEquipment | ProductionQuantity;

interface ICatRecordCounterOnly {
  Counter: string;
}

export type IBulkEntryType = 'st' | 'ot' | 'dt';

/**
 * @description This class is our most complex one. It contains all logic for managing a timesheet,
 * which is a complex task since timesheet is not something native to SAP,
 * so we do a good bit of work to group the records. */
export class TimesheetHandler {
  //our record types as application objects
  public currentEmployeeCrew: Employee[] = []; //array for employees which holds employee crew with stateful app required logic
  public currentEquipmentCrew: Equipment[] = []; //array for equipment
  public currentQuantityProduction: ProductionQuantity[] = []; //array for numeric production
  public currentTextProduction: ProductionText[] = []; //array for text based production
  public currentEquipmentProduction: ProductionEquipment[] = []; //array for production masking as equipment
  public currentProductionGroups: ProductionGroup[] = []; //array for storing production group skfs as an array

  private deleteBank: Array<IEntity> = []; //this array holds our cats objects when they are deleted.

  public isTimesheetInProgress = false; //true once a timesheet's state has been changed
  public isReadyToRender = false; //true once the required data has been fetched.
  public isTimesheetApproved = false;

  public currentTab = 0; //keeps track of which tab the user was last working on for when we jump around.

  constructor(
    private readonly datahub: DatahubService,
    private readonly dialog: MatDialog,
  ) {
    this.reset();
  }

  public reset(): void {
    //clean the timesheet state. Should be called when moving between days or contracts.
    this.currentTab = 0;
    this.clearAllEmployees();
    this.clearAllEquipments();
    this.currentEmployeeCrew = [];
    this.currentEquipmentCrew = [];
    this.currentQuantityProduction = [];
    this.currentTextProduction = [];
    this.currentEquipmentProduction = [];
    this.currentProductionGroups = [];
    this.isTimesheetInProgress = false;
    this.deleteBank = [];
    this.isReadyToRender = false;
  }

  public bulkEntryEmployees(type: IBulkEntryType): void {
    const ref = this.dialog.open(BulkEntryComponent, { disableClose: true });
    const employeeErrors: string[] = [];
    ref.afterClosed().subscribe((value) => {
      if (value === undefined || value === null || value === '') return;
      this.currentEmployeeCrew.forEach((employee) => {
        const valAsNum = Number(value);
        if (valAsNum + employee.workDateTotal > 24) {
          employeeErrors.push(employee.display);
          return;
        }
        const control = employee.rows[type].hours;
        if (!control.disabled) {
          employee.rows[type].hours.setValue(valAsNum.toString());
        }
      });
      if (employeeErrors.length > 0) {
        const msg =
          employeeErrors.length === 1
            ? `Bulk hours entered for employee <strong>${employeeErrors[0] ?? ''}</strong> exceeds 24 hours`
            : `Bulk hours entered for the following ${employeeErrors.length.toString()} employees exceeds 24 hours: <ul>${employeeErrors.map((emp) => `<li><strong>${emp}</strong></li>`).join('')}</ul>`;
        this.datahub.messageService.error(msg, 'Hours not entered!');
      }
    });
  }

  public bulkEntryEquipment$(): Observable<string | undefined | null> {
    const ref = this.dialog.open(BulkEntryComponent, { disableClose: true });
    return ref.afterClosed();
  }

  public saveCrew(): void {
    //save the crew manifest into local storage.
    const employees = this.currentEmployeeCrew.map((x) => x.EmpId);
    const equipments = this.currentEquipmentCrew.map((x) => x.EquipmentId);
    this.datahub.userService.staticUserData.value.empCrew = employees;
    this.datahub.userService.staticUserData.value.eqpCrew = equipments;
    this.currentEquipmentCrew = this.currentEquipmentCrew.sort((x, y) =>
      Number(x.EquipmentId) > Number(y.EquipmentId) ? 1 : -1,
    );
    this.datahub.userService.syncToCache();
  }

  public clearAllEmployees(): void {
    //remove all employees from the timesheet
    const arrLength = this.currentEmployeeCrew.length;
    if (!arrLength) return;
    for (let i = 0; i < arrLength; i++) {
      this.currentEmployeeCrew[0]?.removeSelf(this.currentEmployeeCrew, true);
    }
  }

  public clearAllEquipments(): void {
    //remove all equipment from the timesheet
    const arrLength = this.currentEquipmentCrew.length;
    if (!arrLength) return;
    for (let i = 0; i < arrLength; i++) {
      this.currentEquipmentCrew[0]?.removeSelf(this.currentEquipmentCrew, true);
    }
  }

  public handleAbandonAttempt$(): Observable<boolean> {
    //function for preventing unwanted date changes.
    if (this.findUnsavedRecords().length === 0) {
      return of(true);
    } else {
      return this.datahub.launchResponsiveModal('confirm-abandon').afterClosed();
    }
  }

  // eslint-disable-next-line rxjs-x/finnish -- Not fixing this name for the ECC app
  public getForemanTimesheet(extDocumentNo: string, date: string) {
    this.reset();
    const approvedStatus = [TimesheetStatusEnum.PayrollApproved, TimesheetStatusEnum.BillingApproved];
    return this.datahub.apiService
      .getForemanTimesheet$({
        extDocumentNum: extDocumentNo,
        entryDate: date,
        user: this.datahub.userService.getEmployeeNumber(),
        startingEmployees: this.datahub.userService.staticUserData.value.empCrew ?? [],
        startingEquipment: this.datahub.userService.staticUserData.value.eqpCrew ?? [],
      })
      .pipe(
        map((response) => {
          const { contract, conditions, extDocument } = response.config;
          const {
            employees,
            equipment,
            productionGroupRecords,
            productionTextRecords,
            productionQuantityRecords,
            productionEquipmentRecords,
          } = response.data;
          this.datahub.setContractAndExtDocument(contract, conditions, extDocument);
          this.isTimesheetApproved = approvedStatus.some((x) => x === this.datahub.extDocument.Status);
          this.currentEmployeeCrew = employees.map((x) => new Employee(x, this.datahub));
          this.currentEquipmentCrew = equipment.map((x) => new Equipment(x, this.datahub));
          this.buildProduction(
            productionGroupRecords,
            productionQuantityRecords,
            productionTextRecords,
            productionEquipmentRecords,
          );
          this.isReadyToRender = true;
          this.isTimesheetInProgress = true;
        }),
      );
  }
  public navigateToSelf(): void {
    this.datahub.navigate(
      `/timesheet?number=${this.datahub.extDocument.Documentno}&date=${this.datahub.date.getSapDateString()}`,
    );
  }

  private buildProduction(
    productionGroupRecords: IForemanCatsProperties[],
    productionQuantityRecords: IForemanCatsProperties[],
    productionTextRecords: IForemanCatsProperties[],
    productionEquipment: IForemanCatsProperties[],
  ): void {
    const production = this.datahub.contract.makeEmptyProduction();
    this.currentProductionGroups = this.setProductionGroupState(productionGroupRecords);
    this.currentQuantityProduction = this.setProductionQuantityState(production.quantity, productionQuantityRecords);
    this.currentTextProduction = this.setProductionTextState(production.text, productionTextRecords);
    this.currentEquipmentProduction = this.setProductionEquipmentState(production.equipment, productionEquipment);
  }

  private setProductionGroupState(groupRecords: IForemanCatsProperties[]): ProductionGroup[] {
    const ids = findUnique<string>(groupRecords, 'ZprdGrpID').sort();
    return ids.map((id) => {
      const records = groupRecords.filter((x) => safeCompare(x.ZprdGrpID, id));
      return new ProductionGroup(this.datahub.contract.getEmptyProductionGroup(id), records);
    });
  }

  private setProductionEquipmentState(
    equipmentList: ISkfConfigEntity[],
    productionEquipmentRecords: IForemanCatsProperties[],
  ): ProductionEquipment[] {
    const productionEquipment: ProductionEquipment[] = [];
    equipmentList.forEach((equipment) => {
      const skf = equipment.SKF;
      let recordsList = productionEquipmentRecords;
      recordsList = recordsList.filter((record) => record.Statkeyfig === skf);
      recordsList.forEach((record, index) => {
        const entity = new ProductionEquipment(equipment);
        entity.activate(index + 1, record);
        productionEquipment.push(entity);
      });
    });
    return productionEquipment;
  }

  private setProductionQuantityState(
    quantity: ProductionQuantity[],
    productionQuantityRecords: IForemanCatsProperties[],
  ): ProductionQuantity[] {
    quantity.forEach((x) => {
      x.findWeeklyTotal(productionQuantityRecords);
    });
    productionQuantityRecords
      .filter((x) =>
        this.datahub.date.isSameDay(this.datahub.date.getMoment(), this.datahub.date.convertSapDateToMoment(x.Workdate)),
      )
      .forEach((record) => {
        const found = quantity.find((x) => x.skfNumber === record.Statkeyfig);
        if (found) found.setState(record);
      });
    return quantity;
  }

  private setProductionTextState(text: ProductionText[], productionTextRecords: IForemanCatsProperties[]): ProductionText[] {
    productionTextRecords.forEach((record) => {
      const found = text.find((x) => x.skfNumber === record.Statkeyfig);
      if (found) found.setState(record);
    });
    return text;
  }

  public addEquipment(equipment: IHttpResponseEquipment): void {
    //once we find equipment information, we can initialize the equipment object with the corresponding record, and add it to the array to track.
    const eqp = new Equipment(
      {
        ...equipment,
        weeklyTotal: '0',
        dailyTotal: '0',
        records: [],
        display: `${equipment.EquipmentId} - ${equipment.Description}`,
      },
      this.datahub,
    );
    this.currentEquipmentCrew.unshift(eqp);
    //Make sure the delete bank contains everything it currently does EXCEPT for the passed in equipment that is being added and we no longer want to delete
    this.deleteBank = this.deleteBank.filter((x) => {
      return !(x instanceof Equipment) || (x instanceof Equipment && x.EquipmentId !== eqp.EquipmentId);
    });
    this.datahub.apiService
      .getOneEquipment$(
        this.datahub.extDocument.Documentno,
        this.datahub.date.getSapDateString(),
        this.datahub.userService.getEmployeeNumber(),
        equipment.EquipmentId,
      )
      .subscribe((hydratedEquipment) => {
        const targetIndex = this.currentEquipmentCrew.findIndex((x) => x.EquipmentId === hydratedEquipment.EquipmentId);
        if (targetIndex > -1) {
          this.currentEquipmentCrew[targetIndex] = new Equipment(hydratedEquipment, this.datahub);
        }
      });
  }

  /**@description used when adding an employee to the timesheet. */
  public addEmployee(employee: IForemanEmployee): void {
    const emp = new Employee(employee, this.datahub);
    this.currentEmployeeCrew.unshift(emp);
    //Make sure the delete bank contains everything it currently does EXCEPT for the passed in employee that is being added and we no longer want to delete
    this.deleteBank = this.deleteBank.filter((x) => !(x instanceof Employee) || (x instanceof Employee && x.EmpId !== emp.EmpId));
  }

  public addProductionGroup$(): Observable<ProductionGroup> {
    return this.datahub.apiService.getProductionGroupId$().pipe(
      map((x) => this.datahub.contract.getEmptyProductionGroup(x)),
      map((x) => {
        const group = new ProductionGroup(x, []);
        this.datahub.timesheet.currentProductionGroups.push(group);
        return group;
      }),
    );
  }

  public findTokenEmployee(): string {
    //This is called when application records are serialized into cats records.
    //Equipment and production require an employee number, but that employee is not related to the record.
    //This method should find that token employee.
    return this.datahub.userService.getEmployeeNumber();
  }

  private findUnsavedRecords(): Array<
    | IForemanCatsProperties
    | IModifyCatsRecordHours
    | IModifyCatsRecordQuantity
    | IModifyCatsRecordText
    | ICreateEmployeeBillable
    | ICreateEquipment
    | ICreateProductionEquipment
    | ICreateProductionQuantity
    | ICreateProductionText
  > {
    const records = [
      ...this.findCatsRecords(this.currentEmployeeCrew),
      ...this.findCatsRecords(this.currentEquipmentCrew),
      ...this.findCatsRecords(this.currentQuantityProduction),
      ...this.findCatsRecords(this.currentTextProduction),
      ...this.findCatsRecords(this.currentEquipmentProduction),
      ...this.findCatsRecords(this.currentProductionGroups),
    ];
    return records;
  }

  private findCatsRecords(array: Array<IEntity>): Array<IAnyCatsRecord> {
    //each record type has a method called 'getCatsRecords' that will return the appropriate properties for each record.
    const records: Array<
      | IAnyCatsRecord
      | Array<
          | ICreateEmployeeBillable
          | IModifyCatsRecordHours
          | ICreateProductionQuantity
          | ICreateProductionText
          | IModifyCatsRecordQuantity
          | IModifyCatsRecordText
        >
    > = [];
    array.forEach((element) => {
      const record = element.getCatRecords(this.datahub);
      if (record) records.push(record); //filter out any null values
    });
    return records.flat();
  }

  public flagForDelete(catsObject: IEntity): void {
    this.deleteBank.push(catsObject);
  }

  private findRecordsForDeletion(): ICatRecordCounterOnly[] {
    return this.deleteBank
      .map((catsObj) => catsObj.findCounters())
      .flat() //make into a one-dimensional array since some methods return arrays and other return plain strings
      .filter((x) => x !== '') //remove empty values
      .map((Counter) => {
        return { Counter };
      });
  }

  public deleteDay(): void {
    this.deleteBank = this.deleteBank.concat(
      this.currentEmployeeCrew,
      this.currentEquipmentCrew,
      this.currentQuantityProduction,
      this.currentTextProduction,
    );
    const deleteObj: IOutgoingCatRecords<ICatRecordCounterOnly> = {
      d: {
        Profile: 'ZCATS',
        Testrun: false,
        ReleaseData: true,
        Mode: CatsActionModeEnum.Delete,
        CatsHeadtoItem: this.findRecordsForDeletion(),
      },
    };
    this.datahub.apiService.postRecords$(deleteObj).subscribe(() => {
      this.datahub.extDocument.Status = TimesheetStatusEnum.InProgress;
      this.datahub.extDocument.updateSelf(this.datahub.apiService);
      this.datahub.messageService.success(`Successfully deleted all records for ${this.datahub.date.getSapDateString()}`);
      this.reset();
      this.datahub.navigate('/contract');
    });
  }

  private makeCatsObject<T>(records: T[], action: CatsActionModeEnum): IOutgoingCatRecords<T> {
    return {
      d: {
        Profile: 'ZCATS',
        ReleaseData: true,
        Mode: action,
        CreatedBy: this.datahub.userService.getEmployeeNumber(),
        CatsHeadtoItem: records,
      },
    };
  }

  public upload$(): Observable<any> {
    //this method is called when the timesheet needs to be synchronized with cats. Unsure weather odata structuring should occur on the webserver or on the client side still.
    const records = this.findUnsavedRecords();
    if (!records.length && this.deleteBank.length < 1) {
      this.datahub.messageService.warning("You haven't made any changes to this timesheet.");
      return of();
    }
    const updateRecords = records.filter((x) => Object.hasOwn(x, 'Counter'));
    const insertRecords = records.filter((x) => !Object.hasOwn(x, 'Counter'));
    const deleteRecords = this.findRecordsForDeletion();
    const updateObject = this.makeCatsObject(updateRecords, CatsActionModeEnum.Update);
    const insertObj = this.makeCatsObject(insertRecords, CatsActionModeEnum.Insert);
    const deleteObj = this.makeCatsObject(deleteRecords, CatsActionModeEnum.Delete);
    const update$ = this.datahub.apiService.postRecords$(updateObject);
    const insert$ = this.datahub.apiService.postRecords$(insertObj);
    const del$ = this.datahub.apiService.postRecords$(deleteObj);

    return del$
      .pipe(
        switchMap(() => {
          //don't return anything from deleted records, it messes it up bad. SwitchMap will run to the new observable
          return insert$.pipe(
            concatMap((creates) => {
              //once we are done with creating records, Concat map will combine the observables together
              return update$.pipe(
                map((updates) => {
                  //start web request for updated ones, map will perform synchronous operations once the updated ones are complete
                  return creates.concat(updates); //combine updates and creates together and return. regular concat
                }),
              );
            }),
          );
        }),
      )
      .pipe(
        map((value) => {
          this.datahub.extDocument.Status = TimesheetStatusEnum.InProgress;
          this.datahub.extDocument.updateSelf(this.datahub.apiService);
          this.saveCrew();
          this.datahub.messageService.success('Successfully updated and created CATS records.');
          this.reset();
          this.datahub.navigate('/contract');
          return value;
        }),
      );
  }
}
/* eslint-enable max-lines -- Temp until we can get this file to be under 300 lines long! */
